/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-08
 */

import {
  Box,
  Container,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';

// Add local dependencies.
import Header from '../../layout/Header';
import { StudioHeader } from '../../navigation/studio';
import { useGetStudioQuery } from '../../../features/studios/studiosApi';
import { useStudioPermissions } from '../../../hooks/useStudioPermissions';
import LiveRegion from '../../common/LiveRegion';
import { ARIA_ROLES } from '../../../utils/accessibility';
import { StudioMembersTab } from '../../studios/members';

/**
 * Studio members page component.
 * Displays and manages studio members with roles and permissions.
 * Only accessible to users with member management permissions.
 *
 * @example
 * return (
 *   <MembersPage />
 * )
 * @returns {React.ReactElement} component.
 */
const MembersPage: React.FC = () => {
  const { studioId } = useParams<{ studioId: string }>();

  // Fetch studio data
  const {
    data: studio,
    isLoading: isLoadingStudio,
    error: studioError,
  } = useGetStudioQuery(studioId!, {
    skip: !studioId,
  });

  // Check permissions
  const {
    permissions,
    role,
    isLoading: isLoadingPermissions,
  } = useStudioPermissions();

  // Handle loading states
  if (isLoadingStudio || isLoadingPermissions) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader loading />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Loading studio members, please wait..."
            politeness="POLITE"
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '50vh',
              gap: 2,
            }}
          >
            <CircularProgress size={50} />
            <Typography variant="h6" color="text.secondary">
              Loading members...
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  // Handle errors
  if (studioError || !studio) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader studio={null} />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Error loading studio information. Please try refreshing the page."
            politeness="ASSERTIVE"
          />
          <Alert severity="error" sx={{ mb: 4 }} role="alert">
            {studioError &&
            'status' in studioError &&
            studioError.status === 404
              ? 'Studio not found. Please check the URL and try again.'
              : 'Failed to load studio information. Please try refreshing the page.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  // Check if user has permission to view members
  if (!permissions?.canViewMembers) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader
          studio={studio}
          userRole={role}
          permissions={permissions}
        />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Access denied. You don't have permission to view members."
            politeness="ASSERTIVE"
          />
          <Alert severity="warning" sx={{ mb: 4 }} role="alert">
            You don&apos;t have permission to view members for this studio.
            Please contact your studio administrator if you believe this is an
            error.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />
      <StudioHeader studio={studio} userRole={role} permissions={permissions} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Live region for successful data load */}
        <LiveRegion
          message={`Studio members loaded successfully for ${studio.name}`}
          politeness="POLITE"
        />

        {/* Page Content */}
        <Box
          role={ARIA_ROLES.MAIN}
          aria-label="Studio members management"
          sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          {/* Page Header */}
          <Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Studio Members
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage members, roles, and permissions for {studio.name}
            </Typography>
          </Box>

          {/* Members Tab Component */}
          <StudioMembersTab studioId={studioId!} />
        </Box>
      </Container>
    </Box>
  );
};

// Export component.
export default MembersPage;
